import React, { useState, useEffect } from "react";
import {
  Mail,
  MessageSquare,
  // Eye, // Reserved for future view details feature
  Trash2,
  CheckCircle,
  AlertCircle,
  Archive,
  RefreshCw,
  Inbox,
  User,
} from "lucide-react";
import { contactService } from "../services/apiService";

const BRAND_COLOR = "#166534";

const ContactsPage = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({
    status: "",
    priority: "",
    search: "",
    sortBy: "createdAt",
    sortOrder: "desc",
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pages: 1,
    total: 0,
    hasNext: false,
    hasPrev: false,
  });
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [actionLoading, setActionLoading] = useState(false);

  // API: Fetches contacts with filters and pagination
  const fetchContacts = async (page = 1, newFilters = filters) => {
    setLoading(true);
    setError("");
    try {
      const response = await contactService.getAllContacts({
        page,
        limit: 10,
        ...newFilters,
      });
      // Our backend returns {contacts, pagination, statusCounts}
      setContacts(response.contacts || []);
      setPagination(response.pagination || {
        current: 1,
        pages: 1,
        total: 0,
        hasNext: false,
        hasPrev: false,
      });
      setStatusCounts(response.statusCounts || {});
    } catch (err) {
      setError(err.message || "Unable to load enquiries at this time.");
    } finally {
      setLoading(false);
    }
  };

  // Filter change
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    fetchContacts(1, newFilters);
  };

  // Search
  const handleSearch = (term) => {
    const newFilters = { ...filters, search: term };
    handleFilterChange(newFilters);
  };

  // Status update
  const handleStatusUpdate = async (contactId, newStatus) => {
    setActionLoading(true);
    try {
      await contactService.updateContactStatus(contactId, {
        status: newStatus,
        respondedBy: undefined, // Set your own admin ID dynamically if available
        respondedAt: new Date().toISOString(),
      });
      await fetchContacts(pagination.current);
    } catch (err) {
      setError(err.message || "An error occurred while updating contact status");
    } finally {
      setActionLoading(false);
    }
  };

  // Delete one
  const handleDeleteContact = async (contactId) => {
    if (!window.confirm("Are you sure you want to delete this contact?")) return;
    setActionLoading(true);
    try {
      await contactService.deleteContact(contactId);
      await fetchContacts(pagination.current);
      setSelectedContacts((prev) => prev.filter((id) => id !== contactId));
    } catch (err) {
      setError(err.message || "An error occurred while deleting contact");
    } finally {
      setActionLoading(false);
    }
  };

  // Delete many
  const handleBulkDelete = async () => {
    if (
      !window.confirm(
        `Are you sure you want to delete ${selectedContacts.length} contacts?`
      )
    )
      return;
    setActionLoading(true);
    try {
      await Promise.all(selectedContacts.map((id) => contactService.deleteContact(id)));
      setSelectedContacts([]);
      await fetchContacts(pagination.current);
    } catch (err) {
      setError(err.message || "An error occurred while deleting contacts");
    } finally {
      setActionLoading(false);
    }
  };

  // Table row select
  const handleContactSelect = (contactId) => {
    setSelectedContacts((prev) =>
      prev.includes(contactId)
        ? prev.filter((id) => id !== contactId)
        : [...prev, contactId]
    );
  };

  // Badge styles
  const getStatusBadgeColor = (status) => {
    switch (status) {
      case "new":
        return `bg-[${BRAND_COLOR}]/10 text-[${BRAND_COLOR}] border border-[${BRAND_COLOR}]/20`;
      case "read":
        return `bg-[${BRAND_COLOR}]/20 text-[${BRAND_COLOR}] border border-[${BRAND_COLOR}]/30`;
      case "responded":
        return `bg-[${BRAND_COLOR}] text-white border border-[${BRAND_COLOR}]`;
      case "archived":
        return "bg-gray-100 text-gray-600 border border-gray-200";
      default:
        return `bg-[${BRAND_COLOR}]/10 text-[${BRAND_COLOR}] border border-[${BRAND_COLOR}]/20`;
    }
  };

  const getPriorityBadgeColor = (priority) => {
    switch (priority) {
      case "high":
        return "bg-red-100 text-red-800 border border-red-200";
      case "medium":
        return "bg-yellow-100 text-yellow-800 border border-yellow-200";
      case "low":
        return `bg-[${BRAND_COLOR}]/10 text-[${BRAND_COLOR}] border border-[${BRAND_COLOR}]/20`;
      default:
        return "bg-gray-100 text-gray-700 border border-gray-200";
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Initial load
  useEffect(() => {
    fetchContacts();
    // eslint-disable-next-line
  }, []);

  // Loader
  if (loading && contacts.length === 0) {
    return (
      <div className="min-h-screen bg-white p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <RefreshCw className={`h-8 w-8 text-[${BRAND_COLOR}] animate-spin mx-auto mb-4`} />
            <p className={`text-[${BRAND_COLOR}] font-medium`}>Loading enquiries...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className={`bg-white rounded-xl p-6 shadow border border-[${BRAND_COLOR}]/20`}>
            <h1 className={`text-3xl font-semibold text-[${BRAND_COLOR}] mb-1`}>
              Enquiry Management
            </h1>
            <p className={`text-[${BRAND_COLOR}]/80 text-base`}>
              Manage and respond to enquiry form submissions
            </p>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start shadow-sm">
            <AlertCircle className="h-5 w-5 text-red-500 mr-3 mt-0.5" />
            <div className="flex-1">
              <p className="text-red-800 font-medium">{error}</p>
              <button
                onClick={() => setError("")}
                className="mt-2 text-sm text-red-600 hover:text-red-800 font-medium"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className={`bg-white p-4 rounded-lg shadow border border-[${BRAND_COLOR}]/20`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-xs font-medium text-[${BRAND_COLOR}]/80`}>
                  Total Enquiries
                </p>
                <p className={`text-2xl font-semibold text-[${BRAND_COLOR}]`}>
                  {pagination.total}
                </p>
              </div>
              <div className={`p-2 bg-[${BRAND_COLOR}]/10 rounded-lg`}>
                <Mail className={`h-6 w-6 text-[${BRAND_COLOR}]`} />
              </div>
            </div>
          </div>
          <div className={`bg-white p-4 rounded-lg shadow border border-[${BRAND_COLOR}]/20`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-xs font-medium text-[${BRAND_COLOR}]/80`}>
                  New Messages
                </p>
                <p className={`text-2xl font-semibold text-[${BRAND_COLOR}]`}>
                  {statusCounts.new || 0}
                </p>
              </div>
              <div className={`p-2 bg-[${BRAND_COLOR}]/10 rounded-lg`}>
                <MessageSquare className={`h-6 w-6 text-[${BRAND_COLOR}]`} />
              </div>
            </div>
          </div>
          <div className={`bg-white p-4 rounded-lg shadow border border-[${BRAND_COLOR}]/20`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-xs font-medium text-[${BRAND_COLOR}]/80`}>
                  Responded
                </p>
                <p className={`text-2xl font-semibold text-[${BRAND_COLOR}]`}>
                  {statusCounts.responded || 0}
                </p>
              </div>
              <div className={`p-2 bg-[${BRAND_COLOR}]/10 rounded-lg`}>
                <CheckCircle className={`h-6 w-6 text-[${BRAND_COLOR}]`} />
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-600">
                  Archived
                </p>
                <p className="text-2xl font-semibold text-gray-800">
                  {statusCounts.archived || 0}
                </p>
              </div>
              <div className="p-2 bg-gray-100 rounded-lg">
                <Archive className="h-6 w-6 text-gray-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className={`bg-white rounded-lg shadow border border-[${BRAND_COLOR}]/20 overflow-hidden`}>
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className={`text-lg font-semibold text-[${BRAND_COLOR}]`}>
                Enquiry Messages
              </h2>
              {selectedContacts.length > 0 && (
                <div className="flex items-center gap-2">
                  <span className={`text-xs text-[${BRAND_COLOR}]/80 font-medium`}>
                    {selectedContacts.length} selected
                  </span>
                  <button
                    onClick={handleBulkDelete}
                    className="px-2 py-1 text-xs text-red-600 hover:text-red-800 font-medium"
                    disabled={actionLoading}
                  >
                    Delete Selected
                  </button>
                </div>
              )}
            </div>

            {/* Filters */}
            <div className="mb-4 flex flex-col md:flex-row gap-3">
              <div className="relative flex-1">
                <input
                  type="text"
                  placeholder="Search enquiries..."
                  className={`w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[${BRAND_COLOR}] focus:border-[${BRAND_COLOR}]`}
                  onChange={e => handleSearch(e.target.value)}
                />
                <span className="absolute left-3 top-2.5 text-gray-400">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none"
                    viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </span>
              </div>
              <div className="flex gap-2">
                <select
                  className={`border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[${BRAND_COLOR}] focus:border-[${BRAND_COLOR}]`}
                  value={filters.status}
                  onChange={e => handleFilterChange({ ...filters, status: e.target.value })}
                >
                  <option value="">All Status</option>
                  <option value="new">New</option>
                  <option value="read">Read</option>
                  <option value="responded">Responded</option>
                  <option value="archived">Archived</option>
                </select>
                <select
                  className={`border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[${BRAND_COLOR}] focus:border-[${BRAND_COLOR}]`}
                  value={filters.priority}
                  onChange={e => handleFilterChange({ ...filters, priority: e.target.value })}
                >
                  <option value="">All Priorities</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>
            </div>

            {contacts.length === 0 && !loading ? (
              // Empty state
              <div className="text-center py-12">
                <div className="max-w-sm mx-auto">
                  <div className={`w-16 h-16 bg-[${BRAND_COLOR}]/10 rounded-full flex items-center justify-center mx-auto mb-4`}>
                    <Inbox className={`h-8 w-8 text-[${BRAND_COLOR}]/50`} />
                  </div>
                  <h3 className={`text-lg font-medium text-[${BRAND_COLOR}] mb-2`}>
                    No enquiries found
                  </h3>
                  <p className={`text-[${BRAND_COLOR}]/60 text-sm mb-4`}>
                    There are currently no enquiries to display. New messages from your contact form will appear here.
                  </p>
                  <button
                    onClick={() => fetchContacts()}
                    className={`inline-flex items-center gap-2 px-4 py-2 bg-[${BRAND_COLOR}]/10 text-[${BRAND_COLOR}]
                      rounded-lg hover:bg-[${BRAND_COLOR}]/20 transition-colors text-sm font-medium`}
                  >
                    <RefreshCw className="h-4 w-4" />
                    Refresh
                  </button>
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className={`border-b border-[${BRAND_COLOR}]/20`}>
                      <th className="text-left py-3 px-3 font-semibold text-[${BRAND_COLOR}] text-sm">
                        <input
                          type="checkbox"
                          className={`rounded text-[${BRAND_COLOR}] focus:ring-[${BRAND_COLOR}]`}
                          checked={selectedContacts.length === contacts.length && contacts.length > 0}
                          onChange={e => {
                            if (e.target.checked) {
                              setSelectedContacts(contacts.map(c => c._id));
                            } else {
                              setSelectedContacts([]);
                            }
                          }}
                        />
                      </th>
                      <th className="text-left py-3 px-3 font-semibold text-[${BRAND_COLOR}] text-sm">Contact</th>
                      <th className="text-left py-3 px-3 font-semibold text-[${BRAND_COLOR}] text-sm">Subject</th>
                      <th className="text-left py-3 px-3 font-semibold text-[${BRAND_COLOR}] text-sm">Status</th>
                      <th className="text-left py-3 px-3 font-semibold text-[${BRAND_COLOR}] text-sm">Priority</th>
                      <th className="text-left py-3 px-3 font-semibold text-[${BRAND_COLOR}] text-sm">Date</th>
                      <th className="text-left py-3 px-3 font-semibold text-[${BRAND_COLOR}] text-sm">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contacts.map((contact) => (
                      <tr
                        key={contact._id}
                        className={`border-b border-[${BRAND_COLOR}]/10 hover:bg-[${BRAND_COLOR}]/5 transition-colors`}
                      >
                        <td className="py-3 px-3">
                          <input
                            type="checkbox"
                            className={`rounded text-[${BRAND_COLOR}] focus:ring-[${BRAND_COLOR}]`}
                            checked={selectedContacts.includes(contact._id)}
                            onChange={() => handleContactSelect(contact._id)}
                          />
                        </td>
                        <td className="py-3 px-3">
                          <div className="flex items-center gap-2">
                            <div className={`w-10 h-10 bg-[${BRAND_COLOR}]/10 rounded-full flex items-center justify-center`}>
                              <User className={`h-5 w-5 text-[${BRAND_COLOR}]`} />
                            </div>
                            <div>
                              <p className={`font-semibold text-[${BRAND_COLOR}] text-sm`}>
                                {contact.name}
                              </p>
                              <p className={`text-xs text-[${BRAND_COLOR}]/80`}>
                                {contact.email}
                              </p>
                              {contact.phone && (
                                <p className={`text-xs text-[${BRAND_COLOR}]/60`}>
                                  {contact.phone}
                                </p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-3">
                          <p className={`font-medium text-[${BRAND_COLOR}] text-sm`}>
                            {contact.subject || "No subject"}
                          </p>
                          <p className={`text-xs text-[${BRAND_COLOR}]/80 line-clamp-2`}>
                            {contact.message.length > 100
                              ? contact.message.substring(0, 100) + "..."
                              : contact.message}
                          </p>
                        </td>
                        <td className="py-3 px-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(contact.status)}`}>
                            {contact.status}
                          </span>
                        </td>
                        <td className="py-3 px-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityBadgeColor(contact.priority)}`}>
                            {contact.priority}
                          </span>
                        </td>
                        <td className="py-3 px-3">
                          <p className={`text-xs text-[${BRAND_COLOR}]/80 font-medium`}>
                            {formatDate(contact.createdAt)}
                          </p>
                        </td>
                        <td className="py-3 px-3">
                          <div className="flex items-center gap-1">
                            {/* Mark as Responded */}
                            <button
                              onClick={() => handleStatusUpdate(contact._id, "responded")}
                              className={`p-1.5 rounded transition-colors ${
                                contact.status === "responded"
                                  ? `text-[${BRAND_COLOR}] bg-[${BRAND_COLOR}]/20`
                                  : `text-[${BRAND_COLOR}]/70 hover:text-[${BRAND_COLOR}] hover:bg-[${BRAND_COLOR}]/10`
                              }`}
                              title="Mark as responded"
                              disabled={contact.status === "responded" || actionLoading}
                            >
                              <CheckCircle className="h-4 w-4" />
                            </button>
                            {/* Archive */}
                            <button
                              onClick={() => handleStatusUpdate(contact._id, "archived")}
                              className={`p-1.5 rounded transition-colors ${
                                contact.status === "archived"
                                  ? "text-gray-600 bg-gray-100"
                                  : "text-gray-600 hover:text-gray-800 hover:bg-gray-100"
                              }`}
                              title="Archive"
                              disabled={contact.status === "archived" || actionLoading}
                            >
                              <Archive className="h-4 w-4" />
                            </button>
                            {/* Delete */}
                            <button
                              onClick={() => handleDeleteContact(contact._id)}
                              className="p-1.5 text-red-600/80 hover:text-red-800 hover:bg-red-100/50 rounded transition-colors"
                              title="Delete"
                              disabled={actionLoading}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className={`px-4 py-3 border-t border-[${BRAND_COLOR}]/20 bg-[${BRAND_COLOR}]/5`}>
              <div className="flex items-center justify-between">
                <p className={`text-xs text-[${BRAND_COLOR}]/80 font-medium`}>
                  Showing {(pagination.current - 1) * 10 + 1} to{" "}
                  {Math.min(pagination.current * 10, pagination.total)} of{" "}
                  {pagination.total} enquiries
                </p>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => fetchContacts(pagination.current - 1)}
                    disabled={!pagination.hasPrev}
                    className={`px-3 py-1 text-xs text-[${BRAND_COLOR}]/80 hover:text-[${BRAND_COLOR}] disabled:opacity-50 disabled:cursor-not-allowed font-medium`}
                  >
                    Previous
                  </button>
                  {Array.from({ length: pagination.pages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => fetchContacts(page)}
                      className={`px-3 py-1 text-xs rounded font-medium transition-colors ${
                        page === pagination.current
                          ? `bg-[${BRAND_COLOR}] text-white shadow`
                          : `text-[${BRAND_COLOR}]/80 hover:text-[${BRAND_COLOR}] hover:bg-[${BRAND_COLOR}]/10`
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => fetchContacts(pagination.current + 1)}
                    disabled={!pagination.hasNext}
                    className={`px-3 py-1 text-xs text-[${BRAND_COLOR}]/80 hover:text-[${BRAND_COLOR}] disabled:opacity-50 disabled:cursor-not-allowed font-medium`}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactsPage;
